/**
 * Map Gesture Handlers for Search Results Map
 *
 * Pure DOM/map-event delegation — no Redux, no CSS, no business logic. Translates the
 * browser and Google Maps SDK events that can only originate from a real user gesture
 * into a single semantic callback, so focusedSearch.plugin can tell a user-driven pan/zoom
 * apart from a programmatic one (fitBounds, setMapZoom, updateMapCenter, etc.).
 *
 * @module searchResultsMap/handlers/mapGestureHandlers
 */

const ZOOM_BUTTON_SELECTOR = 'button[title="Zoom in"], button[title="Zoom out"]';

function createMapGestureHandlers() {
  const state = {
    domListeners: [],
    mapEventUnsubscribers: [],
    onUserGesture: null,
  };

  function notifyUserGesture() {
    state.onUserGesture?.();
  }

  function onClick(event) {
    if (event.target.closest(ZOOM_BUTTON_SELECTOR)) {
      notifyUserGesture();
    }
  }

  function onTouchStart(event) {
    // A single-finger touchstart is just a tap (e.g. selecting a marker) and doesn't
    // move the camera on its own — pan-by-drag is already covered by 'dragstart' below.
    // Only 2+ fingers signals the start of a pinch-zoom gesture.
    if (event.touches.length >= 2) {
      notifyUserGesture();
    }
  }

  /**
   * @param {ReturnType<import('libraries/googleMapsAdapter').default>['createFacade']} mapFacade
   * @param {HTMLElement} mapElement - The map's container element.
   * @param {object} callbacks
   * @param {() => void} callbacks.onUserGesture - Called whenever a real user gesture
   *   that changes the camera (drag, scroll zoom, pinch zoom, double-click zoom, or a
   *   zoom-control click) is detected. Isolated taps/clicks that don't move the map
   *   (e.g. selecting a marker) are deliberately excluded.
   */
  function bind(mapFacade, mapElement, { onUserGesture }) {
    state.onUserGesture = onUserGesture;

    mapElement.addEventListener('wheel', notifyUserGesture, { passive: true });
    mapElement.addEventListener('touchstart', onTouchStart, { passive: true });
    mapElement.addEventListener('click', onClick);
    state.domListeners.push(
      () => mapElement.removeEventListener('wheel', notifyUserGesture),
      () => mapElement.removeEventListener('touchstart', onTouchStart),
      () => mapElement.removeEventListener('click', onClick),
    );

    state.mapEventUnsubscribers.push(
      mapFacade.onMapEvent('dragstart', notifyUserGesture),
      mapFacade.onMapEvent('dblclick', notifyUserGesture),
    );
  }

  function unbind() {
    state.domListeners.forEach((removeListener) => removeListener());
    state.domListeners = [];
    state.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe());
    state.mapEventUnsubscribers = [];
    state.onUserGesture = null;
  }

  return { bind, unbind };
}

export default createMapGestureHandlers;
